import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import i18n from "i18next";

import { getProjectSettings } from "../../modules/settings";
import { siteMode } from "../../cores/siteMode";

import { EntryLoader } from "../../components/loader";
import TokenExpiredModal from "../../components/modals/TokenExpired";
import menuService from "@services/menuService";
import useSettings from "@hooks/useSettings";
import { ENTRY_SCREEN, ENTRY_SCREEN_V2 } from "@routes/admins/settings/constants";
import { loadCustomMenus, resetCustomMenus } from "@modules/custom-menus";
import AccessDenied from "@routes/errors/Error403";
import { CUSTOM_MENU_DOMAIN, CUSTOM_MENU_TYPE } from "@constants";
import { useLocation } from "react-router";

function EntryConsole() {
    const history = useHistory();
    const dispatch = useDispatch();
    const isCms = siteMode.get() === "CMS";
    const location = useLocation();

    const { updateLocaleLanguage } = useSettings({ type: "General" });

    const settings = useSelector(({ settings }) => settings);
    const global = useSelector(({ global }) => global);

    const [siteError, setSiteError] = useState(false);
    //Note: 이 컴포넌트는 SPA 로도 진입한다(하드 리로드가 아니라 redux 가 살아 있다). 그래서 이전 세션의
    //customMenus 가 남아 있고, 아래 [customMenus] 효과가 «이번 마운트에서 메뉴를 다시 받기 전에» 그 값으로
    //이동하면 이미 없어진 customMenuSets 경로로 갔다가 되돌아오면서 Page not found 가 잠깐 뜬다
    //(관리자에서 커스텀 → 기본으로 바꾼 직후). makeMenus 가 최신 메뉴를 채운 뒤에만 이동을 허용한다.
    const [customMenusReady, setCustomMenusReady] = useState(false);
    const { stage, project, customMenus } = useSelector(
        (state) => ({
            stage: state.stage,
            project: state.project,
            customMenus: state.customMenus,
        }),
        shallowEqual,
    );

    if (!project.id) history.push(`${menuService.paths.spaces()}`);

    function getSiteMode(userProject) {
        const currentSiteMode = siteMode.get();
        const isConsole = currentSiteMode === "CONSOLE";
        const siteAccess = userProject.siteAccess;
        const hasConsoleAccess = siteAccess.indexOf("CONSOLE") !== -1;
        const hasCmsAccess = siteAccess.indexOf("CMS") !== -1;
        const targetSite =
            (isConsole && hasConsoleAccess) || (!isConsole && !hasCmsAccess && hasConsoleAccess)
                ? "CONSOLE"
                : hasCmsAccess
                ? "CMS"
                : null;
        return targetSite;
    }

    function redirect(redirectPath) {
        // 현재 버전(stage.version)? 이 더 높으면 해당 영역 수행
        if (stage.user && stage.user.projects) {
            // project 가 있어야지만 해당 페이지 접근 가능
            const userProject = stage.user.projects.filter((p) => p.id === project.id)[0];
            if (!userProject) {
                setSiteError(true);
                return;
            }
            const targetSite = getSiteMode(userProject);
            switch (targetSite) {
                case "CONSOLE": {
                    //Note: 사이트 모드가 그대로면 전체 새로고침이 필요 없다 — 진입에 필요한 설정·메뉴는
                    //makeMenus() 가 이 시점에 이미 redux 에 넣었고, 하드 내비게이션은 그걸 다시 받을 뿐이다.
                    //SPA 로 두면 브라우저가 전송 중인 업로드(자산 생성·저장소)가 끊기지 않고,
                    //내부 이동인데 이탈 경고가 뜨는 문제도 사라진다.
                    //모드가 바뀌는 경우(/library 로 들어왔지만 CMS 권한이 없어 CONSOLE 로 보내는 케이스)는
                    //siteMode 가 pathname 파생이라(cores/siteMode.js) 마운트된 트리가 반대 모드를
                    //가정하고 있다 — 그때는 하드 내비게이션으로 앱을 새로 띄운다.
                    if (isCms) {
                        window.location.replace(`${window.location.origin}${redirectPath}`);
                    } else {
                        history.replace(redirectPath);
                    }
                    break;
                }
                case "CMS": {
                    //Note: /library 접두사는 siteMode 가 달라 앱을 새로 띄워야 한다 — 하드 내비게이션 유지.
                    window.location.replace(`${window.location.origin}/library${redirectPath}`);
                    break;
                }
                default: {
                    // case null
                    setSiteError(true);
                    break;
                }
            }
        }
    }

    const redirectCustomMenus = useCallback(() => {
        const findTopLevelMenu = (menus) => {
            for (let menu of menus) {
                if (menu.domain) return menu;
                if (menu.subMenus && menu.subMenus.length > 0) {
                    const result = findTopLevelMenu(menu.subMenus);
                    if (result) return result;
                }
            }
            return undefined;
        };

        const findDomainMenu = (menus, domain, resourceId) => {
            for (let menu of menus) {
                if (menu.domain === domain) {
                    if (menu.domain !== CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                        return menu;
                    }
                    if (menu.type === CUSTOM_MENU_TYPE.LIST || menu.searchParams?.collections?.[0]?.id === resourceId) {
                        //Note: 이동하려는 컬렉션인 경우 상세 페이지가 메뉴로 사용되었을 경우도 있을 수 있어서 resourceId로 동일한 메뉴를 찾아야한다.
                        return menu;
                    }
                }
                if (menu.subMenus && menu.subMenus.length > 0) {
                    const result = findDomainMenu(menu.subMenus, domain, resourceId);
                    if (result) return result;
                }
            }
            return undefined;
        };

        const parseDetailTypeMenu = (menu) => {
            if (menu?.type !== CUSTOM_MENU_TYPE.DETAIL) return "";

            if (menu?.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                return `/${menu?.searchParams.collections?.[0]?.id}`;
            }
            return "";
        };

        const parseDetailTypeLinkFromMenu = (domain, menu, resourceId) => {
            if (domain === CUSTOM_MENU_DOMAIN.ASSETS) return `/customMenuSets/common/assets/assets/${resourceId}`;

            if (domain === CUSTOM_MENU_DOMAIN.COLLECTIONS) {
                return `/customMenuSets/${customMenus.setId}/${menu?.id || ""}/${resourceId}`;
            }
            if (domain === "PROJECT") {
                return `/customMenuSets/${customMenus.setId}/${menu?.id || ""}${resourceId ? `/${resourceId}` : ""}`;
            }
            return "/customMenuSets/"; //Note: Custom Contents의 경우 보내줄 주소가 없으며, customMenuSets를 안 붙이면 App.js와 무한루프를 돌게되어서 추가했습니다.
        };

        const searchParams = new URLSearchParams(location.search);
        const linkFromNoti = searchParams.get("linkFromNoti");

        if (linkFromNoti) {
            const domain = searchParams.get("customMenuDomain");
            const resourceId = searchParams.get("resourceId");

            let detailMenuFromLink = "";
            if (domain === "PROJECT") {
                //Note: 프로젝트 댓글의 경우 메뉴가 정해지지 않아 첫 메뉴 찾는 로직을 그대로 사용
                const menuFromLink = findTopLevelMenu(customMenus.data);
                const resourceId =
                    menuFromLink.domain === CUSTOM_MENU_DOMAIN.COLLECTIONS
                        ? menuFromLink?.searchParams?.collections?.[0]?.id
                        : "";
                detailMenuFromLink = parseDetailTypeLinkFromMenu(domain, menuFromLink, resourceId);
            } else {
                const menuFromLink = findDomainMenu(customMenus.data, domain, resourceId);
                detailMenuFromLink = parseDetailTypeLinkFromMenu(domain, menuFromLink, resourceId);
            }
            const linkMenuPath = `/spaces/${stage.id}/projects/${project.id}${detailMenuFromLink}${location.search}`;
            redirect(linkMenuPath);
            return;
        }
        const defaultMenu =
            customMenus.data?.[0]?.subMenus?.length > 0 ? customMenus.data[0].subMenus[0] : customMenus.data?.[0];

        const topLevelMenu = findTopLevelMenu(customMenus.data) ?? defaultMenu;

        const path = `/spaces/${stage.id}/projects/${project.id}/customMenuSets/${customMenus.setId}/${
            topLevelMenu?.id
        }${parseDetailTypeMenu(topLevelMenu)}`;
        redirect(path);
    }, [customMenus]);

    const redirectDefaultMenus = async (settings) => {
        const entryScreenValue = JSON.parse(settings?.General?.entryScreen?.value || "{}");
        const redirectPath =
            entryScreenValue?.path === ENTRY_SCREEN.CREATE_ASSETS.values?.[0]?.path
                ? ENTRY_SCREEN_V2.CREATE_ASSETS.values?.[0]?.path
                : entryScreenValue?.path;

        redirect(
            redirectPath
                ? `/spaces/${stage.id}/projects/${project.id}${redirectPath}`
                : `/spaces/${stage.id}/projects/${project.id}/dashboard`,
        );
    };

    const makeMenus = useCallback(async () => {
        try {
            const settings = await dispatch(
                getProjectSettings({
                    apiEndpoint: stage.endpoint,
                    stageId: stage.id,
                    projectId: project.id,
                }),
            );

            // 언어 설정
            const consoleDefaultLanguage = settings?.General?.consoleDefaultLanguage?.value;
            if (consoleDefaultLanguage) {
                const isUpdatedLocale = !!localStorage.getItem(`i18nLng`);

                if (!isUpdatedLocale) {
                    await i18n.changeLanguage(consoleDefaultLanguage).then(() => {
                        updateLocaleLanguage(consoleDefaultLanguage);
                    });
                }
            }

            const isCustomMenu = settings.MenuName?.selectMenu?.value === "custom";
            if (isCustomMenu) {
                const setId = location.pathname.split("/customMenuSets/")?.[1]?.split("/")?.[0];
                await dispatch(loadCustomMenus({ isLibrary: isCms, setId }));
                setCustomMenusReady(true);
            } else {
                //Note: 기본 메뉴로 바뀐 경우 이전 세션의 커스텀 메뉴를 여기서 비운다 — 아래 효과는
                //customMenusReady 가 false 라 돌지 않으므로 정리를 효과에 맡길 수 없다.
                dispatch(resetCustomMenus());
                await redirectDefaultMenus(settings);
            }
        } catch (e) {
            console.error(e);
        }
    }, [stage, project]);

    useEffect(() => {
        if (!customMenusReady) return;
        if (!customMenus.data) return;
        if (customMenus.data && customMenus.projectId !== project.id) return;
        if (settings?.project?.MenuName?.selectMenu?.value !== "custom") {
            dispatch(resetCustomMenus());
            return;
        }
        redirectCustomMenus();
    }, [customMenus, customMenusReady]);

    useEffect(() => {
        if (!stage || !project) return;

        makeMenus();
    }, [stage, project]);

    if (!global.isAgreementRequired && customMenus?.error) {
        return <AccessDenied />;
    }
    return (
        <>
            {siteError ? (
                <TokenExpiredModal
                    error={{ type: "NO_SITE_ACCESS" }}
                    onConfirm={() => {
                        setSiteError(false);
                        history.push(`${menuService.paths.spaces()}`);
                    }}
                />
            ) : (
                <EntryLoader />
            )}
        </>
    );
}

export default EntryConsole;
